import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Upload,
  Image,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import VillageService from "../../../services/VillageService";

const { TextArea } = Input;
const { Option } = Select;

// Bilingual labels for tags
const TAG_LABELS = {
  temple: "Temple - गावातील मंदिरे",
  tourist: "Tourist - पर्यटन स्थळे",
  water: "Water - पाण्याची सुविधा",
  education: "Education - शिक्षण प्रणाली",
  development: "Development - विकास कार्य",
  smallbusiness: "Small Business - लघु उद्योग",
  administration: "Administration - प्रशासकीय सेवा",
  other: "Other - इतर माहिती",
};

// Helper function to get label for a tag
const getTagLabel = (tagName) => {
  const key = tagName?.toLowerCase();
  return TAG_LABELS[key] || tagName || "Other";
};

const VillageInfo = () => {
  const [list, setList] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [form] = Form.useForm();

  const fetchSections = async () => {
    try {
      const res = await VillageService.getVillageSections();
      setSections(res.data || []);
      return res.data || [];
    } catch (err) {
      console.error(err);
      message.error("Failed to load village sections");
      return [];
    }
  };

  const fetch = async (sectionsData) => {
    setLoading(true);
    try {
      const res = await VillageService.getVillageInfos();
      // Find the 'history' section ID to filter it out
      const historySection = (sectionsData || []).find(
        (s) => s.name && s.name.toLowerCase() === "history"
      );
      const historySectionId = historySection ? historySection.id : null;

      // Filter out records that belong to the 'history' section
      const data = (res.data || []).filter(
        (item) => historySectionId != null ? item.tags !== historySectionId : true
      );
      setList(data);
    } catch (err) {
      console.error(err);
      message.error("Failed to load village info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const sectionsData = await fetchSections();
      fetch(sectionsData);
    };
    init();
  }, []);

  // Group data by village_section and sort by order
  const groupedData = useMemo(() => {
    const groups = {};
    list.forEach((item) => {
      // tags from API might be an ID. Find the name from sections list.
      // If the API returns the expanded object, handle that too (unlikely based on description).
      // Assuming item.tags is the ID.
      const sectionId = item.tags;
      const sectionObj = sections.find((s) => s.id === sectionId);
      const groupName = sectionObj ? sectionObj.name : "Other"; // Fallback if section not found

      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(item);
    });

    return Object.entries(groups).map(([groupName, items]) => {
      // Sort items by order (ascending)
      const sortedItems = items.sort((a, b) => (a.order || 0) - (b.order || 0));
      return {
        key: groupName,
        groupName: getTagLabel(groupName),
        items: sortedItems,
      };
    });
  }, [list, sections]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ language: "Marathi" });
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      title: record.title,
      description: record.description,
      order: record.order,
      tags: record.tags, // Set the ID
      language: record.language || "Marathi",
    });
    setSelectedFile(null);
    setPreviewUrl(record.image || null);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete entry",
      content: "Are you sure?",
      async onOk() {
        try {
          await VillageService.deleteVillageInfo(id);
          message.success("Deleted");
          fetch(sections);
        } catch (err) {
          console.error(err);
          message.error("Delete failed");
        }
      },
    });
  };

  const handleView = (record) => {
    setViewing(record);
    setIsViewOpen(true);
  };

  const beforeUpload = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      message.error("Invalid image type. Allowed: jpg, png, webp");
      return false;
    }
    if (file.size > maxSize) {
      message.error("Image must be smaller than 5MB");
      return false;
    }
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return false; // prevent default upload
  };

  const onFinish = async (values) => {
    console.log(values);
    const payload = {
      title: values.title,
      description: values.description,
      order: values.order != null ? values.order : 0,
      tags: values.tags,
      language: values.language || "Marathi",
    };

    setLoading(true);
    try {
      if (editing) {
        await VillageService.updateVillageInfo(
          editing.id,
          payload,
          selectedFile
        );
        message.success("Updated");
      } else {
        await VillageService.createVillageInfo(payload, selectedFile);
        message.success("Created");
      }
      setIsModalOpen(false);
      form.resetFields();
      setSelectedFile(null);
      if (
        previewUrl &&
        previewUrl.startsWith &&
        previewUrl.startsWith("blob:")
      ) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch (e) {}
      }
      setPreviewUrl(null);
      fetch(sections);
    } catch (err) {
      console.error(err);
      message.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (t) => (
        <div style={{ maxWidth: 200, whiteSpace: "normal" }}>{t}</div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (t) => (
        <div
          style={{
            maxWidth: 300,
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {t}
        </div>
      ),
    },
    { title: "Order", dataIndex: "order", key: "order" },
    { title: "Language", dataIndex: "language", key: "language" },
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (v) => (v ? <Image src={v} width={80} /> : null),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(r)}
          >
            View
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEdit(r)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(r.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 12,
        }}
      >
        <h3 style={{ margin: 0 }}>My Village (माझे गाव)</h3>
        <div style={{ flexShrink: 0 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add
          </Button>
        </div>
      </div>

      {groupedData.length === 0 ? (
        <Table columns={columns} dataSource={[]} loading={loading} />
      ) : (
        groupedData.map((group) => (
          <div key={group.key} style={{ marginBottom: 24 }}>
            <div
              style={{
                background: "linear-gradient(90deg, #001529 0%, #042035 100%)",
                padding: "12px 16px",
                borderRadius: "8px 8px 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: "white", fontWeight: 600, fontSize: 16 }}>
                {group.groupName}
              </span>
              <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 14 }}>
                Total Items: {group.items.length}
              </span>
            </div>
            <Table
              columns={columns}
              dataSource={group.items}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
              style={{ borderRadius: "0 0 8px 8px", overflow: "hidden" }}
            />
          </div>
        ))
      )}

      <Modal
        title={editing ? "Edit Village Info" : "Create Village Info"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setSelectedFile(null);
          if (
            previewUrl &&
            previewUrl.startsWith &&
            previewUrl.startsWith("blob:")
          ) {
            try {
              URL.revokeObjectURL(previewUrl);
            } catch (e) {}
          }
          setPreviewUrl(null);
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Enter title" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={6} />
          </Form.Item>

          <Form.Item label="Image">
            <Upload
              beforeUpload={beforeUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>Choose File</Button>
            </Upload>
            {previewUrl && (
              <div style={{ marginTop: 8 }}>
                <img src={previewUrl} alt="preview" style={{ maxWidth: 200 }} />
              </div>
            )}
          </Form.Item>

          <Form.Item name="order" label="Order">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="tags" label="Village Section">
            <Select
              showSearch
              placeholder="Select sections"
              optionFilterProp="children"
            >
              {sections
                .filter((s) => s.name.toLowerCase() !== "history")
                .map((s) => (
                  <Option key={s.id} value={s.id}>
                    {getTagLabel(s.name)}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item name="language" label="Language" initialValue="Marathi">
            <Select allowClear>
              <Option value="English">English</Option>
              <Option value="Marathi">Marathi</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editing ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Village Info"
        open={isViewOpen}
        onCancel={() => setIsViewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {viewing && (
          <div>
            <h4>{viewing.title}</h4>
            {viewing.image && (
              <p>
                <img
                  src={viewing.image}
                  alt="cover"
                  style={{ maxWidth: 300 }}
                />
              </p>
            )}
            <div dangerouslySetInnerHTML={{ __html: viewing.description }} />
            <p>
              <strong>Village Section:</strong>{" "}
              {getTagLabel(sections.find((s) => s.id === viewing.tags)?.name) ||
                viewing.tags}
            </p>
            <p>
              <strong>Language:</strong> {viewing.language}
            </p>
            <p>
              <strong>Order:</strong> {viewing.order}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VillageInfo;
